import { Box, Container, Typography, Paper, Stack } from '@mui/material';
import React from 'react';

const steps = [
  {
    icon: '/assets/images/landing/search1.png',
    title: 'Describe Your Project',
    description:
      "Tell us about your home project or problem in detail. Whether it's a quick fix, emergency repair, or major renovation - be as specific as you like.",
  },
  {
    icon: '/assets/images/landing/search2.png',
    title: 'AI Finds Perfect Matches',
    description:
      'Our AI analyzes your needs and searches our network of verified providers to find the best matches for your specific project and location.',
  },
  {
    icon: '/assets/images/landing/search3.png',
    title: 'Terri Contacts Providers',
    description:
      'Your AI assistant reaches out to selected providers on your behalf, explaining your project requirements and requesting quotes.',
  },
  {
    icon: '/assets/images/landing/search4.png',
    title: 'Compare & Choose',
    description:
      'Receive personalized quotes or follow up questions. Compare providers based on pricing, reviews, and expertise to make the best choice.',
  },
];

const VendorHowItWorksSection: React.FC = () => (
  <Box
    sx={{
      py: { xs: 8, md: 12 },
      backgroundColor: '#F8FAFC',
    }}
  >
    <Container maxWidth="lg">
      <Stack spacing={6} alignItems="center">
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              color: 'text.primary',
            }}
          >
            How It Works
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              fontSize: '1.125rem',
              maxWidth: '600px',
            }}
          >
            From problem to solution in four simple steps. Let Terri handle the legwork while you
            focus on what matters most.
          </Typography>
        </Stack>

        {/* Image and Steps Side by Side */}
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={6}
          alignItems="center"
          sx={{ maxWidth: '1400px', width: '100%' }}
        >
          {/* Left side - Image */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <img
              src="/assets/images/landing/terri_search.png"
              alt="How It Works"
              style={{
                width: '100%',
                height: 'auto',
                maxWidth: '500px',
                borderRadius: '12px',
              }}
            />
          </Box>

          {/* Right side - Steps in 2x2 grid */}
          <Box sx={{ flex: 1 }}>
            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                {steps.slice(0, 2).map((step, index) => (
                  <Stack key={step.title} sx={{ flex: 1 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        height: '100%',
                        textAlign: 'center',
                        backgroundColor: 'white',
                        border: '1px solid',
                        borderColor: 'grey.200',
                        borderRadius: 3,
                        position: 'relative',
                        '&:hover': {
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                          transform: 'translateY(-2px)',
                          transition: 'all 0.3s ease-in-out',
                        },
                      }}
                    >
                      <Stack spacing={2} alignItems="center">
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            backgroundColor: 'primary.50',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                          }}
                        >
                          {typeof step.icon === 'string' ? (
                            <img
                              src={step.icon}
                              alt={step.title}
                              style={{
                                width: 84,
                                height: 84,
                                borderRadius: '4px',
                                objectFit: 'cover',
                              }}
                            />
                          ) : null}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -6,
                              right: -6,
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              backgroundColor: 'primary.main',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                            }}
                          >
                            {index + 1}
                          </Box>
                        </Box>

                        <Stack spacing={1} textAlign="center">
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: 'text.primary',
                              fontSize: '1rem',
                            }}
                          >
                            {step.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              lineHeight: 1.5,
                              fontSize: '0.85rem',
                            }}
                          >
                            {step.description}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Paper>
                  </Stack>
                ))}
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                {steps.slice(2, 4).map((step, index) => (
                  <Stack key={step.title} sx={{ flex: 1 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        height: '100%',
                        textAlign: 'center',
                        backgroundColor: 'white',
                        border: '1px solid',
                        borderColor: 'grey.200',
                        borderRadius: 3,
                        position: 'relative',
                        '&:hover': {
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                          transform: 'translateY(-2px)',
                          transition: 'all 0.3s ease-in-out',
                        },
                      }}
                    >
                      <Stack spacing={2} alignItems="center">
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            backgroundColor: 'primary.50',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                          }}
                        >
                          {typeof step.icon === 'string' ? (
                            <img
                              src={step.icon}
                              alt={step.title}
                              style={{
                                width: 84,
                                height: 84,
                                borderRadius: '4px',
                                objectFit: 'cover',
                              }}
                            />
                          ) : null}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -6,
                              right: -6,
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              backgroundColor: 'primary.main',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                            }}
                          >
                            {index + 3}
                          </Box>
                        </Box>

                        <Stack spacing={1} textAlign="center">
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: 'text.primary',
                              fontSize: '1rem',
                            }}
                          >
                            {step.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              lineHeight: 1.5,
                              fontSize: '0.85rem',
                            }}
                          >
                            {step.description}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Paper>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Box>
        </Stack>

        {/* Benefits Section */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={4}
          sx={{ maxWidth: '1200px', width: '100%', mt: 4 }}
        >
          <Box
            sx={{
              flex: 1,
              p: 4,
              backgroundColor: 'primary.50',
              borderRadius: 3,
              textAlign: 'center',
              border: '1px solid',
              borderColor: 'primary.100',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                mb: 2,
              }}
            >
              Why Choose Our AI-Powered Approach?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              Save hours of research, avoid unreliable contractors, and get competitive quotes
              without the hassle. Terri handles everything from initial contact to quote collection.
            </Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              p: 4,
              backgroundColor: 'success.50',
              borderRadius: 3,
              textAlign: 'center',
              border: '1px solid',
              borderColor: 'success.100',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'success.main',
                mb: 2,
              }}
            >
              Your Privacy is Protected
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              Terri protects your privacy by using our dedicated phone number when contacting
              providers, keeping your personal information secure without exposing your contact
              details. We will never sell your data, period.
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </Container>
  </Box>
);

export default VendorHowItWorksSection;
